/**
 * ServerContactVerificationAdapter — the production `ContactVerificationPort`
 * (036). Replaces the browser-local `DemoContactVerificationAdapter`: the
 * "last verified on" fact is now an app-owned server record, so the prompt is
 * durable across devices (SC-007) and the localStorage store is gone (SC-009).
 *
 * Backed by the always-on `member.contactVerification.*` tRPC surface (mounted
 * independently of the IS-client gate — D-179):
 *   - `getVerificationState()` reads `verificationState` and PREFERS the server's
 *     authoritative `isDue` (no client-clock skew). The client-side 12-calendar-
 *     month calc is the fallback for the seed-only contract suite and for a
 *     degraded/absent server.
 *   - `recordVerification('confirmed')` calls `confirmUpToDate`;
 *     `recordVerification('updated')` is a server no-op — the paired
 *     `member.profile.update` already stamped the cadence server-side (FR-013).
 *     A SUCCESSFUL outcome refreshes a local "verified now" cache so a subsequent
 *     `getVerificationState()` reports not-due immediately (read-your-writes); a
 *     FAILED explicit confirm leaves the cache untouched, so the NEXT read (new
 *     session / reload) reflects the still-due server truth and the member is
 *     re-prompted. NOTE: the prompt UI itself closes optimistically on click
 *     (`ContactVerificationPrompt.handleConfirm` — a deliberate low-friction UX
 *     for a yearly nag), so a failed confirm looks resolved WITHIN the session;
 *     the "not a false success" guarantee holds across reloads, not intra-session.
 *
 * Degradation: a missing namespace (`cv === undefined`) is the expected
 * IS-dormant/no-client path and is silent; a real transport THROW falls back to
 * the cached/seeded state AND emits an operator diagnostic (never crashes the
 * gate, never silently hides a persistent outage).
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type {
  ContactVerificationPort,
  ContactVerificationState,
  VerificationOutcome,
} from './ContactVerificationPort.js';

/** Seed the initial `lastVerifiedAt` (used by the contract suite; mirrors the demo adapter). */
export interface ContactVerificationSeed {
  readonly lastVerifiedAt?: string | null;
}

export interface ServerContactVerificationAdapterDeps {
  /** Typed tRPC client. Omitted in the port-contract suite (seed-only). */
  readonly client?: WebTrpcClient;
  /** Deterministic initial state for the contract suite. */
  readonly seed?: ContactVerificationSeed;
  /** Injectable clock (ms). Defaults to `Date.now`; the contract suite uses the default. */
  readonly now?: () => number;
}

/** 12 calendar months, `setMonth(+12)`, `>=` — mirrors the server (D-179 / R3) and the retired adapter. */
function isReverificationDue(lastVerifiedAt: string | null, nowMs: number): boolean {
  if (lastVerifiedAt === null) {
    return true;
  }
  const due = new Date(lastVerifiedAt);
  due.setMonth(due.getMonth() + 12);
  return nowMs >= due.getTime();
}

export class ServerContactVerificationAdapter implements ContactVerificationPort {
  readonly #client: WebTrpcClient | undefined;
  readonly #now: () => number;
  #lastVerifiedAt: string | null;
  // The SERVER's authoritative `isDue` from the last successful read (null =
  // unknown / no client / read failed → fall back to the client-side calc).
  #serverIsDue: boolean | null = null;

  constructor(deps: ServerContactVerificationAdapterDeps = {}) {
    this.#client = deps.client;
    this.#now = deps.now ?? (() => Date.now());
    this.#lastVerifiedAt = deps.seed?.lastVerifiedAt ?? null;
  }

  async getVerificationState(): Promise<ContactVerificationState> {
    const cv = this.#client?.member?.contactVerification;
    if (cv !== undefined) {
      try {
        const state = await cv.verificationState.query();
        this.#lastVerifiedAt = state.lastVerifiedAt;
        // Prefer the SERVER's authoritative `isDue` over a client-clock recompute:
        // the browser clock can be skewed, and two independent 12-month calcs can
        // drift. The client-side calc stays only as the offline/degraded fallback.
        this.#serverIsDue = state.isDue;
      } catch (err) {
        // Real TRANSPORT failure (a missing namespace is the `cv === undefined`
        // branch — expected, silent). Degrade to the client-side calc but leave a
        // diagnostic so a persistent cadence-endpoint outage is visible to
        // operators instead of silently suppressing/forcing the prompt.
        this.#serverIsDue = null;
        // biome-ignore lint/suspicious/noConsole: deliberate observability — a silent read-throw would make a persistent cadence-endpoint outage undiagnosable
        console.warn('ServerContactVerificationAdapter.verificationState query failed', err);
      }
    }
    return {
      lastVerifiedAt: this.#lastVerifiedAt,
      isDue: this.#serverIsDue ?? isReverificationDue(this.#lastVerifiedAt, this.#now()),
    };
  }

  async recordVerification(outcome: VerificationOutcome): Promise<void> {
    const cv = this.#client?.member?.contactVerification;
    // 'confirmed' explicitly stamps; 'updated' is already stamped server-side by
    // the paired member.profile.update (FR-013), so no server call is needed.
    if (cv !== undefined && outcome === 'confirmed') {
      try {
        await cv.confirmUpToDate.mutate();
      } catch (err) {
        // On a FAILED confirm, leave the local cache untouched so the NEXT read
        // (new session / reload) reflects the still-due server truth and the
        // member is re-prompted (self-heals). The prompt UI still closes
        // optimistically this session (deliberate — see handleConfirm); the
        // re-prompt is cross-session, not intra-session. Diagnostic, no PII.
        // biome-ignore lint/suspicious/noConsole: deliberate observability — a silent confirm-throw would hide a failing cadence write
        console.warn('ServerContactVerificationAdapter.confirmUpToDate mutation failed', err);
        return;
      }
    }
    // Read-your-writes: reflect "verified now" locally after a successful confirm
    // (or an 'updated', already stamped server-side by the paired write).
    this.#lastVerifiedAt = new Date(this.#now()).toISOString();
    this.#serverIsDue = false;
  }
}
